/**
 * Lógica pura para el banner "lugar disponible" en la pantalla del partido.
 *
 * Extraída de ViewMatch.tsx para poder testearla de forma aislada.
 */

export interface SpotOpenPlayer {
  userId?: string | null;
  isSubstitute?: boolean;
}

export interface SpotOpenMatch {
  status: string;
  date: string;  // 'YYYY-MM-DD'
  time: string;  // 'HH:MM'
  maxPlayers: number;
  players: SpotOpenPlayer[];
}

export interface SpotOpenBannerResult {
  show: boolean;
  hoursUntilMatch: number;
  openSpots: number;
}

/**
 * Determina si el banner crítico de lugar disponible debe mostrarse.
 *
 * Condiciones (todas deben cumplirse):
 *  1. El partido está en estado SCHEDULED
 *  2. Hay al menos 1 lugar libre entre jugadores confirmados
 *  3. El partido arranca en menos de 4 horas (y no pasó)
 *  4. El usuario actual es un jugador confirmado (no sustituto)
 */
export function computeSpotOpenBanner(
  match: SpotOpenMatch,
  userId: string | null | undefined,
  nowMs: number = Date.now()
): SpotOpenBannerResult {
  const none: SpotOpenBannerResult = { show: false, hoursUntilMatch: 0, openSpots: 0 };

  if (match.status !== 'SCHEDULED') return none;

  const confirmedPlayers = match.players.filter(p => !p.isSubstitute);
  const openSpots = match.maxPlayers - confirmedPlayers.length;
  if (openSpots <= 0) return none;

  const matchDateTime = new Date(`${match.date}T${match.time}`);
  const hoursUntilMatch = (matchDateTime.getTime() - nowMs) / 3_600_000;
  if (hoursUntilMatch <= 0 || hoursUntilMatch >= 4) return { show: false, hoursUntilMatch, openSpots };

  if (!userId) return { show: false, hoursUntilMatch, openSpots };

  const userIsConfirmedPlayer = confirmedPlayers.some(p => p.userId === userId);
  return { show: userIsConfirmedPlayer, hoursUntilMatch, openSpots };
}
